import { type ColumnDef, flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import update from 'immutability-helper';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import type { TdmType } from '@/types/Tdm';
import TableFooterNav from './TableFooterNav';
import TableRow from './TableRow';

interface TableProps {
  tdmType: TdmType;
  cols: ColumnDef<any>[];
  tableData: any;
  onRowClick?: (row: any) => void;
  fetching?: boolean;
  pagination?: {
    page: number;
    pageSize: number;
    pageCount: number;
    currentPage: number;
    setPageSize: (v: number) => void;
    nextPage: () => void;
    previousPage: () => void;
  };
  updateTableData?: (index: number, id: string, value: string | any) => void;
  headerBorderAccent?: boolean;
  defaultColumn?: Partial<ColumnDef<any>>;
  setIsDragged: React.Dispatch<React.SetStateAction<boolean>>;
  setData: React.Dispatch<React.SetStateAction<any>>;
}

const Table = ({
  cols,
  tableData,
  onRowClick,
  pagination,
  fetching,
  headerBorderAccent,
  updateTableData,
  defaultColumn,
  tdmType,
  setIsDragged,
  setData,
}: TableProps) => {
  const columns = useMemo(() => cols, [cols]);
  const data = useMemo(() => tableData, [tableData]);
  const [editableRowIndex, setEditableRowIndex] = useState(null);

  const getRowId = useCallback((row: any) => {
    return row.id;
  }, []);

  const table = useReactTable({
    columns,
    data,
    defaultColumn,
    manualPagination: true,
    pageCount: pagination?.pageCount ?? -1,
    state: {
      pagination: {
        pageIndex: pagination?.page ?? 0,
        pageSize: pagination?.pageSize ?? 10,
      },
    },
    onPaginationChange: () => {},
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId,
    meta: {
      updateTableData,
      editableRowIndex,
      setEditableRowIndex,
    },
  });

  const { getHeaderGroups, getRowModel } = table;
  const headerGroups = getHeaderGroups();
  const rows = getRowModel().rows;

  const moveRow = (dragIndex: number, hoverIndex: number) => {
    const dragData = data[dragIndex];
    const updatedData = update(data, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragData],
      ],
    });

    const newData = updatedData.map((data: any, index: number) => ({
      ...data,
      sortIndex: index + ((pagination?.currentPage ?? 0) - 1) * (pagination?.pageSize ?? 0),
    }));
    setData(newData);
    if (dragData) setIsDragged(true);
  };

  return (
    <>
      <div className='overflow-x-auto w-full h-full'>
        <DndProvider backend={HTML5Backend} context={window}>
          <table className='divide-y divide-gray-200 w-full'>
            <thead className={`bg-gray-50 border-primaryColor-normal ${headerBorderAccent && 'border-l-4'}`}>
              {headerGroups.map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        minWidth: header.column.columnDef.minSize,
                        maxWidth: header.column.columnDef.maxSize,
                        width: header.column.columnDef.size,
                      }}
                      className='p-6 text-left text-xs font-bold text-gray-500 capitalize tracking-wider'
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            {fetching && (
              <tbody className='bg-white divide-y divide-gray-200 animate-pulse'>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <tr key={num}>
                    {Array.from(Array(columns.length).keys()).map((num) => (
                      <td className='p-6' key={num}>
                        <span className='h-2 rounded-md bg-neutralColor-light w-32 block'></span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
            {!fetching && (
              <tbody className='bg-white divide-y divide-gray-200'>
                {rows.map((row, index) => (
                  <TableRow key={row.id} index={index} row={row} moveRow={moveRow} tdmType={tdmType} onRowClick={onRowClick} />
                ))}
              </tbody>
            )}
          </table>
        </DndProvider>
      </div>
      {!pagination && (
        <TableFooterNav
          nextPage={() => table.nextPage()}
          previousPage={() => table.previousPage()}
          currentPage={table.getState().pagination.pageIndex + 1}
          totalPage={table.getPageCount()}
          perPageHandler={(size: number) => table.setPageSize(size)}
        />
      )}
      {pagination && (
        <TableFooterNav
          nextPage={pagination.nextPage}
          previousPage={pagination.previousPage}
          currentPage={pagination.currentPage}
          totalPage={table.getPageCount() || 1}
          perPage={pagination.pageSize}
          perPageHandler={pagination.setPageSize}
        />
      )}
    </>
  );
};

export default Table;
